import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Form from "../../../../components/Form/Form";
import FormTitle from "../../../../components/FormTitle/FormTitle";
import Input from "../../../../components/Input/Input";
import Loader from "../../../../components/Loader/Loader";
import { useAuth } from "../../../../contexts/auth";
import { useListManager } from "../../../../contexts/list.manager";
import "./FormPoliticas.css";
import Select from "../../../../components/Select/Select";
import ToolTip from "../../../../components/Tooltip/Tooltip";
import { PolicyEngine } from "../../../../policy-engine/core";
import Action from "./Action";
import { MdOutlineDelete } from "react-icons/md";
import Button from "../../../../components/Button/Button";
import Condition from "./Condition";
import PolicyService from "../../../../services/PoliciesService";
import { LevelSelector } from "../../../../components/LevelSelector/LevelSelector";
import { ShieldCheck } from "lucide-react";

function FormPoliticas(props) {
	const { user } = useAuth();
	const [name, setName] = useState(props.politica?.name || "");
	const [effect, setEffect] = useState(props.politica?.effect || "");
	const [actions, setActions] = useState([]);
	const [conditions, setConditions] = useState([]);
	const [inNewUser, setInNewUser] = useState(1);
	const [levels, setLevels] = useState([]);
	const [description, setDescription] = useState(props.politica?.descricao || "");
	const [policyEngine, setPolicyEngine] = useState(null);
	const [showLoader, setShowLoader] = useState(false);
	const { closeForm } = useListManager();
	const navigate = useNavigate();

	useEffect(() => {
		setPolicyEngine(PolicyEngine.init(user.userData, user.userData.policies));
	}, [user])

	useEffect(() => {
		setName(props.politica?.name || "");
		setEffect(props.politica?.effect || "");
		setDescription(props.politica?.descricao || "");
		setInNewUser(props.politica?.inNewUser || 1);
		setActions(props.politica?.actions?.map((actionItem) => {
			const [owner, service, scope, ...actionParts] = actionItem.split('.');
			return {
				owner,
				service,
				scope,
				action: actionParts.join('.')
			}
		}) ?? []);
		setLevels(props.politica?.levels?.map(level => ({ unitId: level.unitId, hierarchy: level.childrenAuthorized })) || []);

		let conditionsList = [];
		if (props.politica?.conditions) {
			for (const [operator, attributes] of Object.entries(props.politica.conditions)) {
				for (const [attribute, value] of Object.entries(attributes)) {
					conditionsList = [
						...conditionsList,
						{
							operator,
							attribute,
							value
						}
					];
				}
			}
		}
		setConditions([...conditionsList]);
	}, [props.politica]);

	const buildPayload = () => {
		const conditionsPayload = {};
		conditions.forEach(condition => {
			if (!(condition.operator in conditionsPayload)) {
				conditionsPayload[condition.operator] = {};
			}
			conditionsPayload[condition.operator][condition.attribute] = condition.value;
		});
		return {
			name,
			effect,
			description,
			inNewUser: inNewUser ? Number(inNewUser) : null,
			actions: actions.map(action => `${action.owner}.${action.service}.${action.scope}.${action.action}`),
			conditions: conditionsPayload,
			levels: levels.map(level => ({
				unitId: isNaN(`${level.unitId}`) ? 'root' : `${level.unitId}`,
				childrenAuthorized: level.hierarchy
			})),
		};
	};

	async function handleSubmit(event) {
		event.preventDefault();
		if (props.type === "edicao") {
			handleEdit();
			return;
		}
		setShowLoader(true);
		const payload = buildPayload();
		const response = await PolicyService.createPolicy(payload);
		setShowLoader(false);
		if (response.data.id) navigate("../consulta-politicas-de-acesso", {
			state: { refresh: true }
		});
	}

	async function handleEdit() {
		setShowLoader(true);
		const payload = buildPayload();
		const response = await PolicyService.updatePolicy(
			props.politica.id,
			payload
		);
		setShowLoader(false);
		if (response.data.id) navigate("../consulta-politicas-de-acesso");
	}

	async function handleDelete() {
		setShowLoader(true);
		if (!props.politica) return;
		await PolicyService.deletePolicy(props.politica.id);
		navigate("../consulta-politicas-de-acesso", {
			state: { deletedId: props.politica.id },
		});
		setShowLoader(false);
		closeForm();
	}

	const handleClickEditButton = () => {
		if (!props.politica) return;
		navigate(`/politicas/${props.politica.externalId}`, { state: props.politica });
	};

	function addAction() {
		setActions(actions => {
			return [...actions, { owner: 'bravas' }];
		});
	}

	function removeAction(idx) {
		const act = [
			...actions.slice(0, idx),
			...actions.slice(idx + 1)
		];
		setActions([...act])
	}

	function updateActionField(idx, field, value) {
		const actionIdx = actions.findIndex((action, key) => key === idx)
		actions[actionIdx][field] = value;
		if (['owner'].includes(field)) {
			actions[actionIdx]['service'] = 0;
			if (value === '*') {
				actions[actionIdx]['service'] = '*';
			}
		}
		if (['owner', 'service'].includes(field)) {
			actions[actionIdx]['scope'] = 0;
			if (value === '*') {
				actions[actionIdx]['scope'] = '*';
			}
		}
		if (['owner', 'service', 'scope'].includes(field)) {
			actions[actionIdx]['action'] = 0;
			if (value === '*') {
				actions[actionIdx]['action'] = '*';
			}
		}
		setActions([...actions])
	}

	function addCondition() {
		setConditions(conditions => {
			return [...conditions, {}];
		});
	}

	function removeCondition(idx) {
		const conds = [
			...conditions.slice(0, idx),
			...conditions.slice(idx + 1)
		];
		setConditions([...conds])
	}

	function updateConditionField(idx, field, value) {
		const conditionIdx = conditions.findIndex((action, key) => key === idx)
		conditions[conditionIdx][field] = value;
		setConditions([...conditions])
	}

	return (
		<React.Fragment>
			<FormTitle
				type={props.type}
				icon={<ShieldCheck size={20} />}
				tituloConsulta={props.politica?.name || null}
				tituloCadastro="Cadastro de Política"
				backRoute={"/consulta-politicas-de-acesso"}
			/>
			{showLoader ? (
				<Loader />
			) : (
				<Form
					handleSubmit={handleSubmit}
					type={props.type}
					handleDelete={handleDelete}
					handleClickEdit={handleClickEditButton}
					navigateCancelUrl="../consulta-politicas-de-acesso"
				>
					<div className={`politica-form-grid`}>
						<Input
							name="name"
							id="name"
							labelText="Nome da Política *"
							value={name}
							onChange={({ target }) => setName(target.value)}
							disabled={props.type === "consulta"}
							required={true}
							style={{ marginBottom: "1rem" }}
						/>
						<Select
							name="effect"
							id="effect"
							labelText="Efeito *"
							value={effect ?? 'Allow'}
							onChange={({ target }) =>
								setEffect(target.value)
							}
							disabled={props.type === "consulta"}
							required={true}
							options={[
								{ value: "Allow", text: "Permitir" },
								{ value: "Deny", text: "Negar" },
							]}
							tooltipText={"Efeito sobre as ações"}
							tooltipVisible={props.type !== "consulta"}
						/>
					</div>
					{props.type !== 'consulta' && (
						<div className={`politica-form-grid`}>
							<LevelSelector
								onChange={(levels) => {
									setLevels(levels.map(level => ({
										unitId: level.unitId,
										hierarchy: level.childrenAuthorized
									})));
								}}
								levels={levels.map(level => ({
									unitId: level.unitId,
									childrenAuthorized: level.hierarchy
								}))}
								showRoot={true}
								type={props.type} />
							<Select
								name="inNewUser"
								id="inNewUser"
								labelText="Adicionar para um novo usuário"
								value={inNewUser}
								onChange={({ target }) =>
									setInNewUser(target.value)
								}
								disabled={props.type === "consulta"}
								required={false}
								options={[
									{ value: 1, text: "Nenhum " },
									{ value: 2, text: "Na unidade " },
									{ value: 3, text: "Na hierarquia" },
								]}
								tooltipText={"Quando um novo usuário for criado, adicionar essa política para ele automaticamente quando a opção selecionada for satisfeita."}
								tooltipVisible={props.type !== "consulta"}
							/>
						</div>
					)}
					{props.type !== 'consulta' && (
						<>
							<div className="politica-config">
								<div className="politica-actions">
									<div className="input-tooltip-container">
										Ações
										<ToolTip
											tooltipVisible={true}
											tooltipText={"Ações as quais receberão os efeitos dessa política"}
										/>
									</div>
									<div className="politica-items-container">
										{actions?.length > 0 ? (
											<>
												<div className="politica-labels">
													<span>Fonte</span>
													<span>Serviço</span>
													<span>Escopo</span>
													<span>Ação</span>
												</div>
												{actions.map((action, idx) => (
													<div className="item-row" key={idx}>
														<Action
															owner={action.owner}
															service={action.service}
															scope={action.scope}
															action={action.action}
															onUpdateField={(value, field) => updateActionField(idx, field, value)}
														/>
														<button type="button" className="btn-delete-item" onClick={e => removeAction(idx)}><MdOutlineDelete size={18} /></button>
													</div>
												))}
											</>
										) : (
											<p className="empty-text">Nenhuma ação selecionada, essa política não surtirá efeito.</p>
										)}
										<div className="container-buttons">
											<Button
												className={"button-white btn-add-item"}
												label={"Adicionar"}
												onClick={addAction}
											/>
											{actions?.length > 2 && (
												<Button
													className={"button-red"}
													label={"Remover todos"}
													action="delete"
													onClick={() => setActions([])}
												></Button>
											)}
										</div>
									</div>
								</div>
								<div className="politica-actions">
									<div className="input-tooltip-container">
										Condições
										<ToolTip
											tooltipVisible={true}
											tooltipText={"Condições para essa política surtir efeito nas ações"}
										/>
									</div>
									<div className="politica-items-container">
										{conditions?.length > 0 ? (
											<>
												{conditions.map((condition, idx) => (
													<div className="item-row" key={idx}>
														<Condition
															attribute={condition.attribute}
															operator={condition.operator}
															value={condition.value}
															onUpdateField={(value, field) => updateConditionField(idx, field, value)}
														/>
														<button type="button" className="btn-delete-item" onClick={e => removeCondition(idx)}><MdOutlineDelete size={18} /></button>
													</div>
												))}
											</>
										) : (
											<p className="empty-text">Nenhuma condição criada, essa política aplicará o efeito sem verificações contextuais.</p>
										)}
										<div className="container-buttons">
											<Button
												className={"button-white btn-add-item"}
												label={"Adicionar"}
												onClick={addCondition}
											/>
											{conditions.length > 2 && (
												<Button
													className={"button-red"}
													label={"Remover todos"}
													action="delete"
													onClick={() => setConditions([])}
												></Button>
											)}
										</div>
									</div>
								</div>
							</div>
						</>
					)}
				</Form>
			)}
		</React.Fragment>
	);
}

export default FormPoliticas;
